import { Link, useParams, useHistory } from "react-router-dom"
import React, { useEffect, useState } from "react"
import BackOfficeAPIClient from "../../../clients/BackofficeAPIClient"
import { SignatureList } from "./Types/Class/SignatureList"
import { AppNameParam } from "./Types/SignatureParam"
import { contractStatus, contractStatusBadges } from "./Types/Enum/ContractStatus"
import InitialChoice from "./Components/InitialChoice"

type InitialScope = 'NONE' | 'GROUP' | 'COMPANY'

interface InitialStateResponse {
    scope: InitialScope
}

export default function Index() {
    const backofficeApiClient = new BackOfficeAPIClient()
    const params: AppNameParam = useParams<AppNameParam>()
    const history = useHistory()

    const [initialScope, setInitialScope] = useState<InitialScope | null>(null)
    const [contracts, setContracts] = useState<SignatureList[]>([])

    useEffect(() => {
        fetchInitialState().then()
    }, [])

    const fetchInitialState = async () => {
        try {
            const state = await backofficeApiClient.get<InitialStateResponse>(
                `/interfaces/signatures/${params.app_name}/initial-state`
            )
            setInitialScope(state.scope)
            if (state.scope === 'GROUP') {
                history.push(`/interfaces/signatures/${params.app_name}/groupe`)
                return
            }
            if (state.scope === 'COMPANY') {
                await fetchContract()
            }
        } catch (err) {
            console.error('Erreur lors de la récupération de l\'état initial:', err)
            setInitialScope('NONE')
        }
    }

    const fetchContract = async () => {
        try {
            const contractsData: SignatureList[] = await backofficeApiClient.get<SignatureList[]>('/interfaces/signatures/' + params.app_name + '/societes')
            setContracts(contractsData)
        } catch (err) {
            console.error('Erreur lors de la récupération des comptes:', err)
        }
    }

    const [filterName, setFilterName] = useState('')
    const [showInactive, setShowInactive] = useState(false)
    const [sortColumn, setSortColumn] = useState<keyof SignatureList | ''>('')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

    const handleSort = (column: keyof SignatureList) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortColumn(column)
            setSortDirection('asc')
        }
    }

    const getSortIcon = (column: keyof SignatureList) => {
        if (sortColumn !== column) {
            return <i className="fa fa-sort text-muted ml-1"></i>
        }
        return sortDirection === 'asc'
            ? <i className="fa fa-sort-up ml-1"></i>
            : <i className="fa fa-sort-down ml-1"></i>
    }

    const getSortedSignatures = () => {
        let filtered = contracts.filter(sig => {
            if (!showInactive && sig.Status === 'INACTIVE') {
                return false
            }
            if (filterName) {
                return sig.NomPlaque.toLowerCase().includes(filterName.toLowerCase())
            }
            return true
        })

        if (sortColumn) {
            filtered = [...filtered].sort((a, b) => {
                const aVal = a[sortColumn]
                const bVal = b[sortColumn]

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
                }

                const aStr = String(aVal).toLowerCase()
                const bStr = String(bVal).toLowerCase()

                if (sortDirection === 'asc') {
                    return aStr.localeCompare(bStr)
                } else {
                    return bStr.localeCompare(aStr)
                }
            })
        }

        return filtered
    }

    const handleChangeStatus = async (idPlaque: number, newStatus: string) => {
        try {
            await backofficeApiClient.patch<any>(
                `/interfaces/signatures/${params.app_name}/societes/${idPlaque}/status`,
                { status: newStatus }
            )
            await fetchContract()
        } catch (err) {
            console.error('Erreur lors de la mise à jour du statut:', err)
            alert("Erreur lors de la mise à jour du statut")
        }
    }

    const handleDelete = async (id: number) => {
        if (!confirm('Êtes-vous sûr de vouloir supprimer ce paramétrage ?')) {
            return
        }

        try {
            await backofficeApiClient.delete<any>('/interfaces/signatures/' + params.app_name + '/societes/' + id, {})
            await fetchContract()
        } catch (err) {
            console.error('Erreur lors de la suppression des paramétrages :', err)
            alert("Erreur lors de la suppression des paramétrages ")
        }
    }

    // Chargement de l'état initial
    if (initialScope === null) {
        return (
            <div className="container-fluid">
                <div className="text-center mt-5">
                    <i className="fa fa-spinner fa-spin fa-3x"></i>
                    <p>Chargement...</p>
                </div>
            </div>
        )
    }

    // Aucun paramétrage existant → écran de choix
    if (initialScope === 'NONE') {
        return <InitialChoice />
    }

    // Paramétrage société existant → liste des sociétés
    return (
        <div className="container-fluid mb-2">
            <div className="row">
                <div className="col">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>Oodrive : signature électronique</h2>
                        <Link to={`/interfaces/signatures/${params.app_name}/create`} className="btn btn-success">
                            Ajouter
                        </Link>
                    </div>

                    <div className="mb-3">
                        <div className="form-inline" style={{ gap: '0.5em' }}>
                            <div className="input-group mr-2">
                                <span className="input-group-addon" id="basic-addon1">
                                    <i className="fa fa-filter"></i>
                                </span>
                                <input
                                    type="search"
                                    className="form-control form-control-sm"
                                    value={filterName}
                                    onChange={(e) => setFilterName(e.target.value)}
                                    placeholder="Filtre par société"
                                />
                            </div>
                            <div className="checkbox-inline">
                                <label className="mb-0">
                                    <input
                                        type="checkbox"
                                        checked={showInactive}
                                        onChange={(e) => setShowInactive(e.target.checked)}
                                        className="mr-1"
                                    />{'Afficher les configurations inactives'}
                                </label>
                            </div>
                        </div>
                    </div>

                    <table className="table table-striped table-hover table-sm">
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                                <th
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('IDPlaque')}
                                >
                                    Société
                                    {getSortIcon('IDPlaque')}
                                </th>
                                <th
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('CreditsRestant')}
                                >
                                    Crédits restants
                                    {getSortIcon('CreditsRestant')}
                                </th>
                                <th>Licences actives (par site)</th>
                                <th>Relances</th>
                                <th
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('Status')}
                                >
                                    Statut
                                    {getSortIcon('Status')}
                                </th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedSignatures().map((signature) => (
                                <tr key={signature.IDSignatureParametre}>
                                    <td>{signature.NomPlaque}</td>
                                    <td>{signature.CreditsRestant}</td>
                                    <td>{signature.LicencesActives}</td>
                                    <td>{signature.Relances}</td>
                                    <td>
                                        <span className={contractStatusBadges[contractStatus[signature.Status]]}>
                                            {contractStatus[signature.Status]}
                                        </span>
                                    </td>
                                    <td className="text-right">
                                        <div className="dropdown" style={{ display: 'inline-block' }}>
                                            <button
                                                className="btn btn-secondary btn-sm dropdown-toggle"
                                                type="button"
                                                id={`dropdownMenu-${signature.IDSignatureParametre}`}
                                                data-toggle="dropdown"
                                                aria-haspopup="true"
                                                aria-expanded="false"
                                            >
                                                <i className="fa fa-ellipsis-v"></i>
                                            </button>
                                            <div className="dropdown-menu dropdown-menu-right" aria-labelledby={`dropdownMenu-${signature.IDSignatureParametre}`}>
                                                {signature.Status === "BLOCKED" && (
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleChangeStatus(signature.IDPlaque, "ACTIVE")}
                                                    >
                                                        Réactiver
                                                    </button>
                                                )}
                                                {signature.Status === "INACTIVE" && (
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleChangeStatus(signature.IDPlaque, "ACTIVE")}
                                                    >
                                                        Activer
                                                    </button>
                                                )}
                                                {signature.Status === "ACTIVE" && (
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleChangeStatus(signature.IDPlaque, "INACTIVE")}
                                                    >
                                                        Désactiver
                                                    </button>
                                                )}
                                                <Link
                                                    className="dropdown-item"
                                                    to={`/interfaces/signatures/${params.app_name}/create?id_plaque=${signature.IDPlaque}`}
                                                >
                                                    Modifier
                                                </Link>
                                                <div className="dropdown-divider"></div>
                                                <button
                                                    className="dropdown-item text-danger"
                                                    onClick={() => handleDelete(signature.IDPlaque)}
                                                >
                                                    Supprimer
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {getSortedSignatures().length === 0 && (
                        <div className="alert alert-info text-center">
                            Aucune signature trouvée
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
